import { memo, useCallback, useMemo } from "react";
import { Bar, Line, LinePath } from "@visx/shape";
import { Group } from "@visx/group";
import { GridRows } from "@visx/grid";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { NumberLike } from "@visx/scale";
import { defaultStyles } from "@visx/tooltip";

import Box from "ds/components/Box";
import useContainerSize from "hooks/useContainerSize";
import { tooltipContainerStyles } from "ds/components/Charts/components/Tooltip/helpers";
import AxisLabel from "ds/components/Charts/components/AxisLabel";

import { WorkerUsageData } from "../types";
import { formatWorkerUsageDate, hasWorkerUsageContract } from "../utils";
import ConcurrencyChartReferenceLine from "./ReferenceLine";
import ConcurrencyChartReferenceLineLabels, { getReferenceLineLabels } from "./ReferenceLineLabels";
import ConcurrencyChartLegend from "./Legend";
import ConcurrencyChartMarker from "./Marker";
import ConcurrencyChartBars from "./Bars";
import ConcurrencyChartTooltip from "./Tooltip";
import useConcurrencyChartLayout from "./useConcurrencyChartLayout";
import useConcurrencyChartHover from "./useConcurrencyChartHover";
import {
  CONCURRENCY_CHART_MARGIN,
  MARKER_LABEL,
  TOOLTIP_OFFSET,
  Y_AXIS_LABEL_OFFSET,
  Y_AXIS_NUM_TICKS,
} from "./constants";
import styles from "./styles.module.css";

type ConcurrencyChartProps = {
  usage: WorkerUsageData;
  summary: string;
};

type PercentilePoint = {
  day: Date;
  value: number;
};

const tooltipStyles = { ...defaultStyles, ...tooltipContainerStyles };

const formatYAxisLabel = (value: NumberLike) => `${value.valueOf()}`;
const formatXAxisLabel = (value: Date | NumberLike) =>
  formatWorkerUsageDate(new Date(value.valueOf()), "d MMM");

const ConcurrencyChart = ({ usage, summary }: ConcurrencyChartProps) => {
  const { containerRef: svgContainerRef, width: parentWidth } = useContainerSize();
  const { containerRef: plotContainerRef, height } = useContainerSize();

  const {
    xScale,
    yScale,
    xMax,
    yMax,
    marginTop,
    todayLabelY,
    effectiveWidth,
    dayBandWidth,
    axisTickValues,
    yAxisCap,
    recordedDays,
    todayX,
    cycleStartX,
    cycleEndX,
  } = useConcurrencyChartLayout(usage, parentWidth, height);

  const {
    hoveredDay,
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    containerRef,
    TooltipInPortal,
    handleMouseMove,
    handleMouseLeave,
    handleScroll,
  } = useConcurrencyChartHover(recordedDays, xScale);

  const hasContract = hasWorkerUsageContract(usage);

  const hasSpikes = useMemo(
    () => recordedDays.some((point) => point.max > yAxisCap),
    [recordedDays, yAxisCap]
  );

  const percentilePoints = useMemo(
    () =>
      usage.daily.reduce<PercentilePoint[]>((acc, point) => {
        if (point.percentileToDateValue !== undefined) {
          acc.push({ day: point.day, value: point.percentileToDateValue });
        }
        return acc;
      }, []),
    [usage.daily]
  );

  const percentileX = useCallback((point: PercentilePoint) => xScale(point.day), [xScale]);
  const percentileY = useCallback((point: PercentilePoint) => yScale(point.value), [yScale]);

  const plotPercentile = hasContract ? usage.contractedPercentile : usage.fallbackPercentile;

  const referenceLabels = useMemo(
    () =>
      getReferenceLineLabels({
        usage,
        lastPercentilePoint: percentilePoints[percentilePoints.length - 1],
        percentile: plotPercentile,
        xScale,
        yScale,
      }),
    [percentilePoints, plotPercentile, usage, xScale, yScale]
  );

  const shouldRender = xMax > 0 && yMax >= 0;

  return (
    <Box
      ref={svgContainerRef}
      direction="column"
      fullWidth
      grow="1"
      gap="medium"
      className={styles.container}
    >
      <ConcurrencyChartLegend
        percentile={plotPercentile}
        hasContract={hasContract}
        hasSpikes={hasSpikes}
      />

      <Box
        ref={plotContainerRef}
        className={styles.plotArea}
        fullWidth
        zeroMinWidth
        grow="1"
        margin="medium 0 0 0"
      >
        <Box fullWidth className={styles.scrollContainer} onScroll={handleScroll}>
          {shouldRender && (
            <svg
              ref={containerRef}
              width={effectiveWidth}
              height={height}
              className={styles.chart}
              role="img"
              aria-label={summary}
              focusable={false}
            >
              <Group top={marginTop} left={CONCURRENCY_CHART_MARGIN.left}>
                <GridRows
                  numTicks={Y_AXIS_NUM_TICKS}
                  scale={yScale}
                  width={xMax}
                  strokeDasharray="1,0"
                  stroke="var(--semantic-color-border-divider)"
                />

                <Bar
                  x={Math.min(cycleStartX, todayX)}
                  y={0}
                  width={Math.max(Math.abs(todayX - cycleStartX), 0)}
                  height={Math.max(yMax, 0)}
                  fill="var(--semantic-color-background-decoration-blue-subdued)"
                  opacity={0.25}
                  pointerEvents="none"
                />

                <AxisLeft
                  numTicks={Y_AXIS_NUM_TICKS}
                  scale={yScale}
                  hideTicks
                  stroke="var(--semantic-color-foreground-decoration-gray-emphasized)"
                  tickComponent={(tickProps) => <AxisLabel {...tickProps} x={tickProps.x - 5} />}
                  label="Private workers"
                  labelOffset={Y_AXIS_LABEL_OFFSET}
                  labelClassName={styles.axisLabel}
                  tickFormat={formatYAxisLabel}
                />
                <Line
                  from={{ x: 0, y: yMax }}
                  to={{ x: xMax, y: yMax }}
                  stroke="var(--semantic-color-foreground-decoration-gray-emphasized)"
                  strokeWidth={1}
                  pointerEvents="none"
                />
                <AxisBottom
                  scale={xScale}
                  top={yMax}
                  tickValues={axisTickValues}
                  hideTicks
                  hideAxisLine
                  tickFormat={formatXAxisLabel}
                  tickComponent={AxisLabel}
                />

                <ConcurrencyChartBars
                  days={recordedDays}
                  xScale={xScale}
                  yScale={yScale}
                  dayBandWidth={dayBandWidth}
                  xMax={xMax}
                  yMax={yMax}
                  yAxisCap={yAxisCap}
                  hoveredDay={hoveredDay}
                />

                {hasContract && (
                  <ConcurrencyChartReferenceLine
                    xMax={xMax}
                    fromX={cycleStartX}
                    toX={cycleEndX}
                    y={yScale(usage.contractedWorkers)}
                    color="var(--concurrency-chart-contract-color)"
                    strokeWidth={1.5}
                  />
                )}

                {percentilePoints.length > 0 && (
                  <LinePath
                    data={percentilePoints}
                    x={percentileX}
                    y={percentileY}
                    stroke="var(--concurrency-chart-p95-color)"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    pointerEvents="none"
                  />
                )}

                {hasContract && (
                  <ConcurrencyChartMarker
                    x={cycleStartX}
                    yMax={yMax}
                    label={MARKER_LABEL.cycleStart}
                  />
                )}
                {hasContract && (
                  <ConcurrencyChartMarker x={cycleEndX} yMax={yMax} label={MARKER_LABEL.cycleEnd} />
                )}
                <ConcurrencyChartMarker
                  x={todayX}
                  yMax={yMax}
                  label={MARKER_LABEL.today}
                  labelY={todayLabelY}
                />

                <ConcurrencyChartReferenceLineLabels labels={referenceLabels} xMax={xMax} />

                {/* Hover works only over the elapsed part of the cycle - on the
                    empty runway there is nothing to point at. Half a band on each
                    side keeps the first and the last bar fully covered. */}
                <Bar
                  x={Math.max(Math.min(cycleStartX, todayX) - dayBandWidth / 2, 0)}
                  y={0}
                  width={Math.max(Math.abs(todayX - cycleStartX) + dayBandWidth, 0)}
                  height={Math.max(yMax, 0)}
                  fill="transparent"
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                />
              </Group>
            </svg>
          )}

          {tooltipOpen && tooltipData && (
            <TooltipInPortal
              top={tooltipTop}
              left={tooltipLeft}
              offsetLeft={TOOLTIP_OFFSET.left}
              offsetTop={TOOLTIP_OFFSET.top}
              style={tooltipStyles}
            >
              <ConcurrencyChartTooltip day={tooltipData} percentile={plotPercentile} />
            </TooltipInPortal>
          )}
        </Box>
      </Box>
    </Box>
  );
};

ConcurrencyChart.displayName = "UsageWorkersNew.ConcurrencyChart";

export default memo(ConcurrencyChart);
